// ============================================
// Auth Guard Component
// ============================================
// Protects routes that require authentication

import { ReactNode, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuthStore } from '@/stores/auth';
import { useTenantStore } from '@/stores/tenant';
import { Loader2 } from 'lucide-react';

interface AuthGuardProps {
  children: ReactNode;
  requireAdmin?: boolean;
  requireTenantAssignment?: boolean;
  fallbackPath?: string;
}

export function AuthGuard({
  children,
  requireAdmin = false,
  requireTenantAssignment = false,
  fallbackPath = '/',
}: AuthGuardProps) {
  const { isAuthenticated, isLoading, user, checkTokenExpiry } = useAuthStore();
  const activeTenantId = useTenantStore(state => state.activeTenantId);
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isStaff = user?.role === 'manager' || user?.role === 'user';

  useEffect(() => {
    // Check token expiry on mount and periodically
    const interval = setInterval(() => {
      if (isAuthenticated) {
        checkTokenExpiry();
      }
    }, 60 * 1000); // Check every minute

    return () => clearInterval(interval);
  }, [isAuthenticated, checkTokenExpiry]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate({ to: '/login' });
    }
  }, [isLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!isLoading && requireAdmin && !isAdmin) {
      // Redirect non-admins trying to access admin routes
      navigate({ to: fallbackPath });
    }
  }, [isLoading, requireAdmin, isAdmin, navigate, fallbackPath]);

  useEffect(() => {
    if (!isLoading && requireTenantAssignment && isStaff && !activeTenantId) {
      navigate({ to: fallbackPath });
    }
  }, [isLoading, requireTenantAssignment, isStaff, activeTenantId, navigate, fallbackPath]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null; // Will redirect in useEffect
  }

  if (requireAdmin && !isAdmin) {
    return null; // Will redirect in useEffect
  }

  if (requireTenantAssignment && isStaff && !activeTenantId) {
    return null; // Will redirect in useEffect
  }

  return <>{children}</>;
}
